import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Envelope, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * "Recibe tu MP3 por correo" card for SongDetail.
 *
 * Pre-fills with user.email if present. Persists email on user record after send.
 * Won't show if the user already received this song at one address recently
 * (controlled via the song.emailed_to list returned from the song document).
 */
export const EmailDeliveryCard = ({ songId, defaultEmail = "", initialSent = false }) => {
  const [email, setEmail] = useState(defaultEmail || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(!!initialSent);

  const submit = async () => {
    const value = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      toast.error("Ingresa un correo válido");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/songs/${songId}/email-me`, { email: value, save_email: true });
      if (data?.already_sent) {
        toast.success("Ya te lo habíamos mandado a ese correo 📬");
      } else {
        toast.success("¡MP3 enviado a tu correo! 📨");
      }
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "No pudimos enviar el correo.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl bg-[#B4F34A]/10 border border-[#B4F34A]/30 p-4 flex items-center gap-3" data-testid="email-delivery-sent">
        <CheckCircle weight="fill" size={22} className="text-[#B4F34A]" />
        <div className="flex-1">
          <p className="text-sm text-[#B4F34A] font-semibold">MP3 enviado a tu correo</p>
          <p className="text-xs text-white/55 mt-0.5">Revisa tu bandeja de entrada (y la carpeta de spam la primera vez).</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-[#121212] border border-white/10 p-4" data-testid="email-delivery-card">
      <div className="flex items-center gap-2 mb-2">
        <Envelope size={18} weight="fill" className="text-[#FFB800]" />
        <p className="font-display font-bold text-sm">Recíbelo en tu correo</p>
      </div>
      <p className="text-xs text-white/55 mb-3">
        Te mandamos el MP3 con portada incrustada y un link directo. Útil si después quieres reenviárselo a alguien.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@correo.com"
          className="flex-1 px-4 py-3 rounded-full bg-[#0a0a0a] border border-white/10 focus:border-[#FFB800] focus:outline-none text-sm placeholder-white/30"
          data-testid="email-delivery-input"
        />
        <button
          onClick={submit}
          disabled={loading}
          className="px-5 py-3 rounded-full bg-[#FFB800] hover:bg-[#E6A600] disabled:opacity-50 text-black font-bold text-sm inline-flex items-center justify-center gap-1.5 active:scale-95 transition-all"
          data-testid="email-delivery-send"
        >
          <PaperPlaneTilt size={16} weight="fill" /> {loading ? "Enviando…" : "Enviar"}
        </button>
      </div>
    </div>
  );
};
